// BILL-02 — Kernvel-staff billing reviews (decided 2026-09-18): the ONLY place a remedy is resolved.
//
// Gated by the Kernvel-staff authority (StaffGate, scope billing.remedies.read to view; the backend
// additionally demands billing.remedies.resolve to act). Staff pick a shop from the cross-tenant
// pointer index (or paste its id), review its verified open remedies and resolve each exactly once:
// waive, charge (settled through the current subscription, bounded by the meter evidence and the
// combined cap) or a Shopify app credit ONLY for an evidenced over-charge. Credits are issued in the
// Shopify Partner Dashboard/API; the issued credit's id is then recorded here and the backend
// verifies it by Admin readback before the receipt reads "credited". Every call is audited.
import { useCallback, useEffect, useState } from "react";

import { staffErrorText, staffGet, staffPost } from "../staff";
import type { OpenRemedy, RemediesData, RemedyDecision, RemedyReceipt, RemedyShop, StaffSession } from "../types";
import { ReceiptSummary, RemedySummary, dollarsToQ4, formatQ4Cents } from "./Remedies";
import StaffGate from "./StaffGate";

function ResolveForm({ shopId, remedy, onDone }: { shopId: string; remedy: OpenRemedy; onDone: () => Promise<void> }) {
  const [decision, setDecision] = useState<RemedyDecision>("waive");
  const [amount, setAmount] = useState("0.00");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bound = decision === "charge" ? remedy.charge_bound_q4 : decision === "credit" ? remedy.credit_bound_q4 : 0;

  const submit = useCallback(async () => {
    const amountQ4 = decision === "waive" ? 0 : dollarsToQ4(amount);
    if (amountQ4 === null || (decision !== "waive" && amountQ4 <= 0)) {
      setErr("Enter a positive dollar amount (whole cents).");
      return;
    }
    if (amountQ4 > bound) {
      setErr(`The evidence bounds a ${decision} at ${formatQ4Cents(bound)}.`);
      return;
    }
    if (!note.trim()) {
      setErr("A note with the evidence reviewed is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await staffPost(`/billing/remedies/${encodeURIComponent(remedy.obligation_ref)}/resolve`, {
        shop_id: shopId, decision, amount_q4: amountQ4, note: note.trim(),
      });
      await onDone();
    } catch (e) {
      setErr(staffErrorText(e, "Resolution failed"));
    } finally {
      setBusy(false);
    }
  }, [amount, bound, decision, note, onDone, remedy.obligation_ref, shopId]);

  return (
    <div className="remedy-form">
      <label>
        Decision{" "}
        <select aria-label="Decision" value={decision} onChange={(e) => setDecision(e.target.value as RemedyDecision)}>
          <option value="waive">Waive (nothing owed)</option>
          <option value="charge" disabled={remedy.charge_bound_q4 === 0}>Charge unbilled usage</option>
          <option value="credit" disabled={remedy.credit_bound_q4 === 0}>App credit (over-charge only)</option>
        </select>
      </label>
      {decision !== "waive" && (
        <label>
          Amount (USD, up to {formatQ4Cents(bound)}){" "}
          <input aria-label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
        </label>
      )}
      <label>
        Note{" "}
        <input aria-label="Note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </label>
      <button className="courier-btn" onClick={() => void submit()} disabled={busy}>
        {busy ? "Saving…" : "Resolve with receipt"}
      </button>
      {err && <span className="dlq-row-err">{err}</span>}
    </div>
  );
}

function CreditForm({ shopId, receipt, onDone }: { shopId: string; receipt: RemedyReceipt; onDone: () => Promise<void> }) {
  const [creditRef, setCreditRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      await staffPost(`/billing/remedies/${encodeURIComponent(receipt.obligation_ref)}/credit`, {
        shop_id: shopId, credit_ref: creditRef.trim(),
      });
      await onDone();
    } catch (e) {
      setErr(staffErrorText(e, "Credit verification failed"));
    } finally {
      setBusy(false);
    }
  }, [creditRef, onDone, receipt.obligation_ref, shopId]);
  return (
    <div className="remedy-form">
      <label>
        Issued app credit id{" "}
        <input aria-label="Credit id" placeholder="gid://shopify/AppCredit/…" value={creditRef}
          onChange={(e) => setCreditRef(e.target.value)} />
      </label>
      <button className="courier-btn" onClick={() => void submit()} disabled={busy || !creditRef.trim()}>
        {busy ? "Verifying…" : "Verify credit"}
      </button>
      {err && <span className="dlq-row-err">{err}</span>}
    </div>
  );
}

function ShopReviews({ shopId, canResolve }: { shopId: string; canResolve: boolean }) {
  const [data, setData] = useState<RemediesData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setData(await staffGet<RemediesData>(`/billing/remedies?shop_id=${encodeURIComponent(shopId)}`));
      setErr(null);
    } catch (e) {
      setData(null);
      setErr(staffErrorText(e, "Failed to load billing reviews"));
    }
  }, [shopId]);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      {err && <div className="banner-err">{err}</div>}
      <section className="lane">
        <h2>{data?.shopify_domain ?? shopId}</h2>
        <div className="lane-body">
          {!data ? (
            <p className="lane-empty">{err ? "Billing reviews unavailable" : "Loading billing reviews…"}</p>
          ) : data.open.length === 0 ? (
            <p className="lane-empty">No open billing reviews.</p>
          ) : (
            data.open.map((remedy) => (
              <div className="dlq-row" key={remedy.obligation_ref}>
                <RemedySummary remedy={remedy} />
                {canResolve && <ResolveForm shopId={shopId} remedy={remedy} onDone={load} />}
              </div>
            ))
          )}
        </div>
      </section>
      {data && data.resolutions.length > 0 && (
        <section className="lane">
          <h2>Receipts</h2>
          <div className="lane-body">
            {data.resolutions.map((receipt) => (
              <div className="dlq-row" key={receipt.receipt_ref}>
                <ReceiptSummary receipt={receipt} />
                {canResolve && receipt.status === "credit_pending" && (
                  <CreditForm shopId={shopId} receipt={receipt} onDone={load} />
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function StaffReviews({ session }: { session: StaffSession }) {
  const [shops, setShops] = useState<RemedyShop[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const [shopId, setShopId] = useState<string | null>(null);
  useEffect(() => {
    staffGet<{ shops: RemedyShop[] }>("/billing/remedies/shops")
      .then((body) => setShops(body.shops))
      .catch((e: unknown) => setErr(staffErrorText(e, "Failed to load the review index")));
  }, []);
  const canResolve = session.scopes.includes("billing.remedies.resolve");
  return (
    <>
      {!canResolve && <p className="cap-note">Read-only: your staff grant has no billing.remedies.resolve.</p>}
      {err && <div className="banner-err">{err}</div>}
      <section className="lane">
        <h2>Shops with open reviews</h2>
        <div className="lane-body">
          {shops === null ? (
            <p className="lane-empty">{err ? "Index unavailable" : "Loading…"}</p>
          ) : shops.length === 0 ? (
            <p className="lane-empty">No shop has an unresolved review.</p>
          ) : (
            shops.map((shop) => (
              <div className="dlq-row" key={shop.shop_id}>
                <div className="dlq-main">
                  <span className="dlq-event">{shop.shop_name || shop.shopify_domain}</span>
                  <span className="dlq-sub">{shop.shopify_domain} · {shop.open_count} open</span>
                </div>
                <button className="courier-btn" onClick={() => setShopId(shop.shop_id)}>
                  Review
                </button>
              </div>
            ))
          )}
        </div>
      </section>
      <div className="card" style={{ margin: "12px 0" }}>
        <label>
          Shop ID{" "}
          <input aria-label="Shop ID" value={typed} onChange={(e) => setTyped(e.target.value)} />
        </label>
        <button className="courier-btn" onClick={() => setShopId(typed.trim())} disabled={!typed.trim()}>
          Open shop
        </button>
      </div>
      {shopId && <ShopReviews key={shopId} shopId={shopId} canResolve={canResolve} />}
    </>
  );
}

export default function StaffRemedies() {
  return (
    <StaffGate title="Billing reviews" scope="billing.remedies.read">
      {(session) => <StaffReviews session={session} />}
    </StaffGate>
  );
}
